import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface NotificationLog {
  id: string;
  collect_id: string;
  notification_type: string;
  recipient: string;
  status: string;
  error_message: string | null;
  sent_at: string;
  created_at: string;
  sender_email: string | null;
  establishment_name: string | null;
  generator_id: string | null;
  generator_name: string | null;
  status_anterior: string | null;
  status_novo: string | null;
  audit_created_at: string | null;
  requester_email: string | null;
}

export const useNotificationLogs = () => {
  return useQuery({
    queryKey: ["notification-logs"],
    queryFn: async () => {
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

      const { data, error } = await supabase
        .from("notification_logs")
        .select(`
          id,
          collect_id,
          notification_type,
          recipient,
          status,
          error_message,
          sent_at,
          created_at,
          sender_email
        `)
        .gte("sent_at", twentyFourHoursAgo.toISOString())
        .order("sent_at", { ascending: false })
        .limit(200);

      if (error) throw error;

      // Buscar dados adicionais de cada collect
      const enrichedData = await Promise.all(
        (data || []).map(async (log) => {
          // Buscar dados da coleta
          const { data: collect } = await supabase
            .from("on_demand_collects")
            .select("establishment_name, generator_id, created_by")
            .eq("id", log.collect_id)
            .single();

          // Buscar nome do gerador
          let generatorName = null;
          if (collect?.generator_id) {
            const { data: generator } = await supabase
              .from("generators")
              .select("generator_name")
              .eq("generator_id", collect.generator_id)
              .single();
            generatorName = generator?.generator_name || null;
          }

          // Buscar a mudança de status mais próxima do horário de envio
          const { data: audit } = await supabase
            .from("on_demand_collects_status_audit")
            .select("status_anterior, status_novo, created_at")
            .eq("collect_id", log.collect_id)
            .order("created_at", { ascending: false })
            .limit(1)
            .single();

          // Extrair email do usuário que criou a coleta (created_by)
          let requesterEmail = null;
          if (collect?.created_by) {
            const createdBy = collect.created_by as Record<string, any>;
            requesterEmail = createdBy?.email || null;
          }

          return {
            ...log,
            sender_email: log.sender_email || null,
            establishment_name: collect?.establishment_name || null,
            generator_id: collect?.generator_id || null,
            generator_name: generatorName,
            status_anterior: audit?.status_anterior || null,
            status_novo: audit?.status_novo || null,
            audit_created_at: audit?.created_at || null,
            requester_email: requesterEmail,
          };
        })
      );

      return enrichedData as NotificationLog[];
    },
    refetchInterval: 3600000, // Atualizar a cada 1 hora
  });
};
